<template>
  <div>
    <div class="d-flex flex-wrap align-center mb-3">
      <span class="kvm-help text-caption mr-auto">Every container on this server, including the ones your apps run.</span>
      <gg-btn small tile outlined color="primary" class="mr-2 mb-1" :loading="loading" @click="load">
        <v-icon left small aria-hidden="true">mdi-refresh</v-icon>Refresh
      </gg-btn>
      <gg-btn small tile color="primary" class="mb-1 kvm-dk-primary" @click="openRun">
        <v-icon left small aria-hidden="true">mdi-play-box-outline</v-icon>Run container
      </gg-btn>
    </div>

    <v-alert v-if="error" type="error" border="left" dense role="alert">{{ error }}</v-alert>

    <div v-if="loading && !loaded" class="py-6 text-center" role="status">
      <v-progress-circular indeterminate color="primary" size="32" width="3" />
      <span class="kvm-sr-only">Loading containers…</span>
    </div>
    <p v-else-if="loaded && !containers.length" class="kvm-help my-6 text-center">
      No containers yet. Run one here, or deploy an app from the Apps tab.
    </p>

    <v-simple-table v-else-if="containers.length" dense class="kvm-dk-table">
      <caption class="kvm-sr-only">Containers</caption>
      <thead>
        <tr>
          <th scope="col">Name</th>
          <th scope="col">Image</th>
          <th scope="col">State</th>
          <th scope="col">Ports</th>
          <th scope="col">App</th>
          <th scope="col" class="text-right"><span class="kvm-sr-only">Actions</span></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="c in containers" :key="c.id">
          <td data-label="Name" class="font-weight-medium">
            {{ c.name }}
            <v-chip v-if="isSystem(c)" x-small label class="ml-1" title="Runs your domains — can be restarted, not stopped or removed">domains</v-chip>
          </td>
          <td data-label="Image" class="kvm-dk-mono">{{ c.image }}</td>
          <td data-label="State">
            <v-chip x-small label :color="stateColor(c.state)" text-color="grey darken-4">{{ c.state }}</v-chip>
            <div class="kvm-help text-caption">{{ c.status }}</div>
          </td>
          <td data-label="Ports" class="kvm-dk-mono text-caption">{{ c.ports || '—' }}</td>
          <td data-label="App">{{ c.stack || '—' }}</td>
          <td data-label="Actions" class="text-right text-no-wrap">
            <gg-btn v-if="c.state !== 'running'" icon small :aria-label="'Start ' + c.name" :disabled="!!busy[c.name]" @click="act(c, 'start')">
              <v-icon small>mdi-play</v-icon>
            </gg-btn>
            <gg-btn v-if="c.state === 'running'" icon small :aria-label="'Restart ' + c.name" :disabled="!!busy[c.name]" @click="act(c, 'restart')">
              <v-icon small>mdi-restart</v-icon>
            </gg-btn>
            <gg-btn v-if="c.state === 'running' && !isSystem(c)" icon small :aria-label="'Stop ' + c.name" :disabled="!!busy[c.name]" @click="act(c, 'stop')">
              <v-icon small>mdi-stop</v-icon>
            </gg-btn>
            <gg-btn icon small :aria-label="'Logs for ' + c.name" @click="openLogs(c)">
              <v-icon small>mdi-text-box-outline</v-icon>
            </gg-btn>
            <gg-btn v-if="!isSystem(c)" icon small :aria-label="'Remove ' + c.name" :disabled="!!busy[c.name]" @click="confirmRemove(c)">
              <v-icon small color="error">mdi-delete-outline</v-icon>
            </gg-btn>
            <v-progress-circular v-if="busy[c.name]" indeterminate size="14" width="2" class="ml-1" />
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <!-- Logs -->
    <v-dialog v-model="logs.open" max-width="900">
      <gg-card role="dialog" aria-modal="true" aria-labelledby="kvm-dk-logs-title">
        <gg-card-text class="pa-4">
          <div class="d-flex align-center mb-2">
            <h3 id="kvm-dk-logs-title" class="body-1 mr-auto">Logs — {{ logs.name }}</h3>
            <v-select v-model="logs.tail" :items="[100, 200, 500, 1000, 2000]" dense outlined hide-details label="Lines"
                      class="mr-2" style="max-width: 110px;" @change="loadLogs" />
            <gg-btn small tile outlined color="primary" :loading="logs.loading" @click="loadLogs">
              <v-icon left small aria-hidden="true">mdi-refresh</v-icon>Refresh
            </gg-btn>
          </div>
          <pre class="kvm-dk-logs">{{ logs.text || (logs.loading ? 'Loading…' : 'No log output.') }}</pre>
          <div class="text-right mt-2"><gg-btn text @click="logs.open = false">Close</gg-btn></div>
        </gg-card-text>
      </gg-card>
    </v-dialog>

    <!-- Remove confirm -->
    <v-dialog v-model="remove.open" max-width="440">
      <gg-card v-if="remove.c" role="dialog" aria-modal="true" aria-labelledby="kvm-dk-rm-title">
        <gg-card-text class="pa-4">
          <h3 id="kvm-dk-rm-title" class="body-1 font-weight-medium mb-2">Remove {{ remove.c.name }}?</h3>
          <p class="mb-2">The container is stopped and deleted. Data in named volumes is kept; anything stored only inside the container is lost.</p>
          <p v-if="remove.c.stack" class="kvm-help text-caption mb-3">
            It belongs to the app “{{ remove.c.stack }}” — redeploying the app will create it again.
          </p>
          <div class="text-right">
            <gg-btn text class="mr-2" @click="remove.open = false">Cancel</gg-btn>
            <gg-btn tile color="error" @click="doRemove">Remove</gg-btn>
          </div>
        </gg-card-text>
      </gg-card>
    </v-dialog>

    <!-- Run container -->
    <v-dialog v-model="run.open" max-width="640" persistent>
      <gg-card role="dialog" aria-modal="true" aria-labelledby="kvm-dk-run-title">
        <gg-card-text class="pa-4">
          <h3 id="kvm-dk-run-title" class="body-1 font-weight-medium mb-3">Run a container</h3>
          <v-text-field v-model.trim="run.image" label="Image" placeholder="nginx:latest" outlined dense
                        hint="From Docker Hub (nginx, postgres:17) or a registry (ghcr.io/owner/app:tag)." persistent-hint class="mb-2" />
          <div class="d-flex flex-wrap">
            <v-text-field v-model.trim="run.name" label="Name (optional)" outlined dense class="mr-2" style="min-width: 220px;" />
            <v-select v-model="run.restart" :items="restartOptions" label="Restart" outlined dense style="max-width: 200px;" />
          </div>
          <v-combobox v-model="run.ports" label="Ports" multiple chips small-chips deletable-chips outlined dense append-icon=""
                      hint="host:container, e.g. 8080:80 — press Enter after each" persistent-hint class="mb-2" />
          <v-combobox v-model="run.volumes" label="Volumes" multiple chips small-chips deletable-chips outlined dense append-icon=""
                      hint="name:/path/in/container or /host/path:/path — press Enter after each" persistent-hint class="mb-2" />
          <div class="subtitle-2 mt-2 mb-1">Environment variables</div>
          <div v-for="(e, i) in run.env" :key="i" class="d-flex align-center">
            <v-text-field v-model.trim="e.key" placeholder="NAME" outlined dense hide-details class="mr-2 mb-2 kvm-dk-mono" style="max-width: 200px;" :aria-label="'Variable ' + (i + 1) + ' name'" />
            <v-text-field v-model="e.value" placeholder="value" outlined dense hide-details class="mr-2 mb-2" :aria-label="'Variable ' + (i + 1) + ' value'" />
            <gg-btn icon small class="mb-2" :aria-label="'Remove variable ' + (i + 1)" @click="run.env.splice(i, 1)"><v-icon small>mdi-close</v-icon></gg-btn>
          </div>
          <gg-btn text small color="primary" class="px-1" @click="run.env.push({ key: '', value: '' })">
            <v-icon left small aria-hidden="true">mdi-plus</v-icon>Add variable
          </gg-btn>
          <v-alert v-if="run.error" type="warning" dense text class="mt-3 mb-0" role="alert">{{ run.error }}</v-alert>
          <div class="text-right mt-3">
            <gg-btn text class="mr-2" :disabled="run.submitting" @click="run.open = false">Cancel</gg-btn>
            <gg-btn tile color="primary" class="kvm-dk-primary" :loading="run.submitting" :disabled="!run.image" @click="doRun">Run</gg-btn>
          </div>
        </gg-card-text>
      </gg-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import kvm from '@/api/kvm'

const blankRun = () => ({ open: true, image: '', name: '', restart: 'unless-stopped', ports: [], volumes: [], env: [], submitting: false, error: '' })

export default {
  name: 'KvmDockerContainers',
  props: {
    serviceId: { type: [String, Number], required: true },
    // Bumped by the parent when an operation finishes → reload.
    refreshKey: { type: Number, default: 0 },
  },
  data: () => ({
    containers: [],
    loading: false,
    loaded: false,
    error: '',
    busy: {},
    logs: { open: false, name: '', tail: 200, text: '', loading: false },
    remove: { open: false, c: null },
    run: { open: false },
    restartOptions: [
      { text: 'Unless stopped', value: 'unless-stopped' },
      { text: 'Always', value: 'always' },
      { text: 'On failure', value: 'on-failure' },
      { text: 'Never', value: 'no' },
    ],
  }),
  watch: {
    refreshKey () { this.load() },
  },
  methods: {
    ...mapActions(['reportSuccess', 'reportError']),
    async load () {
      this.loading = true
      try {
        this.containers = await kvm.dockerContainers(this.serviceId)
        this.error = ''
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
        this.loaded = true
      }
    },
    // The managed web proxy's container (runs the customer's domains).
    isSystem (c) {
      return c.name === 'prox-proxy'
    },
    stateColor (state) {
      if (state === 'running') return 'green lighten-3'
      if (state === 'restarting' || state === 'paused') return 'amber lighten-3'
      if (state === 'dead') return 'red lighten-3'
      return 'grey lighten-2'
    },
    async act (c, action) {
      this.$set(this.busy, c.name, true)
      try {
        await kvm.dockerContainerAction(this.serviceId, c.name, action)
        this.reportSuccess(`${c.name}: ${action === 'remove' ? 'removed' : action + ' done'}.`)
        await this.load()
      } catch (e) {
        this.reportError(e)
      } finally {
        this.$delete(this.busy, c.name)
      }
    },
    confirmRemove (c) {
      this.remove = { open: true, c }
    },
    doRemove () {
      const c = this.remove.c
      this.remove.open = false
      this.act(c, 'remove')
    },
    openLogs (c) {
      this.logs = { open: true, name: c.name, tail: 200, text: '', loading: false }
      this.loadLogs()
    },
    async loadLogs () {
      this.logs.loading = true
      try {
        const r = await kvm.dockerLogs(this.serviceId, this.logs.name, this.logs.tail)
        // docker logs splits the container's stdout/stderr; show both, errors last.
        this.logs.text = [r.stdout, r.stderr].filter(Boolean).join(r.stdout && r.stderr ? '\n— stderr —\n' : '')
      } catch (e) {
        this.logs.text = e.message
      } finally {
        this.logs.loading = false
      }
    },
    openRun () {
      this.run = blankRun()
    },
    async doRun () {
      const r = this.run
      const spec = { image: r.image, restart: r.restart }
      if (r.name) spec.name = r.name
      if (r.ports.length) spec.ports = r.ports.map(p => String(p).trim()).filter(Boolean)
      if (r.volumes.length) spec.volumes = r.volumes.map(v => String(v).trim()).filter(Boolean)
      const env = r.env.filter(e => e.key)
      if (env.length) spec.env = env
      r.submitting = true
      r.error = ''
      try {
        const op = await kvm.dockerRun(this.serviceId, spec)
        this.$emit('operation', op)
        r.open = false
      } catch (e) {
        // Laravel's validation bag → a readable line.
        r.error = e.data && e.data.errors ? Object.values(e.data.errors).flat().join(' ') : e.message
      } finally {
        r.submitting = false
      }
    },
  },
  mounted () {
    this.load()
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.kvm-sr-only { @include kvm-sr-only; }
.kvm-help { @include kvm-secondary-text; }
.kvm-dk-mono { font-family: monospace; font-size: 0.8rem; }
.kvm-dk-primary { @include kvm-btn-primary-aa; }
.kvm-dk-table {
  @include kvm-stacked-table;
  th { white-space: nowrap; }
}
.kvm-dk-logs {
  max-height: 60vh;
  overflow: auto;
  background: #000;
  color: #cfd6dc;
  font-size: 0.78rem;
  line-height: 1.35;
  padding: 8px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-all;
}
::v-deep .v-btn:focus-visible { @include kvm-focus-ring; }
</style>
